import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Alert,
  Stack, FormControl, Select, MenuItem,
  IconButton, Divider, Card,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, useMediaQuery, Pagination,
  Avatar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Refresh, PeopleAlt } from '@mui/icons-material';
import api from '../../services/api';

interface AgenteCliente {
  ID: string;
  TIPO_SERVICO: string;
  CLIENTE_REF: string;
  CLIENTE_NOME: string | null;
  CLIENTE_EMAIL: string | null;
  ESTADO: string;
  DT_INICIO: string;
  totalTransacoes: number;
  totalVolume: number;
  totalComissao: number;
  ultimaTransacao: string | null;
}

const SERVICO_LABEL: Record<string, string> = {
  PAGAMENTO_SISP: 'SISP',
  EVENTS_INSCRICAO: 'Eventos',
  WAG_SUBSCRICAO: 'WAG',
  IFUNDHERE: 'iFundHere',
};

const SERVICO_COLOR: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
  PAGAMENTO_SISP: 'primary',
  EVENTS_INSCRICAO: 'secondary',
  WAG_SUBSCRICAO: 'success',
  IFUNDHERE: 'warning',
};

const fmt = (n: number) =>
  new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(n);

const fmtDate = (dt: string | null) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short', year: 'numeric' });
};

const initials = (nome: string | null, ref: string) => {
  if (nome) return nome.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
  return ref.slice(0, 2).toUpperCase();
};

const PAGE_SIZE = 20;

const ALL_SERVICOS = ['PAGAMENTO_SISP', 'EVENTS_INSCRICAO', 'WAG_SUBSCRICAO', 'IFUNDHERE'];

export default function AgenteClientes() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [clientes, setClientes] = useState<AgenteCliente[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tipoServico, setTipoServico] = useState('');
  const [estado, setEstado] = useState('');

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        page,
      };
      if (tipoServico) params.tipoServico = tipoServico;
      if (estado) params.estado = estado;
      const res = await api.get('/agente/clientes', { params });
      setClientes(res.data?.data || []);
      setTotal(res.data?.pagination?.total || res.data?.data?.length || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  }, [tipoServico, estado, page]);

  useEffect(() => { fetchClientes(); }, [fetchClientes]);

  const handleFilterChange = (field: string, value: string) => {
    setPage(1);
    if (field === 'tipoServico') setTipoServico(value);
    if (field === 'estado') setEstado(value);
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={800}>
              Meus Clientes
            </Typography>
            {total > 0 && (
              <Chip label={total} size="small" color="primary" sx={{ fontWeight: 700, height: 22 }} />
            )}
          </Stack>
          {!loading && (
            <Typography variant="caption" color="text.secondary">
              Clientes associados a si por serviço
            </Typography>
          )}
        </Box>
        <IconButton onClick={fetchClientes} disabled={loading} color="primary">
          <Refresh />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2.5, borderRadius: 2 }}>
        <Box sx={{ p: '12px 16px' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={tipoServico}
                displayEmpty
                onChange={e => handleFilterChange('tipoServico', e.target.value)}
              >
                <MenuItem value="">Todos os serviços</MenuItem>
                {ALL_SERVICOS.map(s => (
                  <MenuItem key={s} value={s}>{SERVICO_LABEL[s]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={estado}
                displayEmpty
                onChange={e => handleFilterChange('estado', e.target.value)}
              >
                <MenuItem value="">Todos os estados</MenuItem>
                <MenuItem value="ATIVO">Activo</MenuItem>
                <MenuItem value="INATIVO">Inactivo</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : clientes.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <PeopleAlt sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              Nenhum cliente encontrado
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {tipoServico || estado
                ? 'Tente ajustar os filtros.'
                : 'Os clientes que angaria aparecem aqui após a primeira transacção processada.'}
            </Typography>
          </Box>
        </Card>
      ) : isMobile ? (
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          {clientes.map((c, idx) => (
            <React.Fragment key={c.ID}>
              {idx > 0 && <Divider />}
              <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: theme.palette[SERVICO_COLOR[c.TIPO_SERVICO] || 'primary'].main,
                    fontSize: 13,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {initials(c.CLIENTE_NOME, c.CLIENTE_REF)}
                </Avatar>
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {c.CLIENTE_NOME || c.CLIENTE_REF}
                  </Typography>
                  {c.CLIENTE_EMAIL && (
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {c.CLIENTE_EMAIL}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                    <Chip
                      label={SERVICO_LABEL[c.TIPO_SERVICO] || c.TIPO_SERVICO}
                      size="small"
                      color={SERVICO_COLOR[c.TIPO_SERVICO] || 'default'}
                      sx={{ fontSize: 10, height: 18 }}
                    />
                    <Chip
                      label={c.ESTADO === 'ATIVO' ? 'Activo' : 'Inactivo'}
                      size="small"
                      color={c.ESTADO === 'ATIVO' ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ fontSize: 10, height: 18 }}
                    />
                  </Stack>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography variant="body2" fontWeight={800} color="success.main">
                    +{fmt(c.totalComissao)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.totalTransacoes} trans.
                  </Typography>
                </Box>
              </Box>
            </React.Fragment>
          ))}
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Serviço</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Email</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Transacções</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Volume</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Comissão</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Última transacção</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientes.map(c => (
                <TableRow key={c.ID} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: theme.palette[SERVICO_COLOR[c.TIPO_SERVICO] || 'primary'].main,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {initials(c.CLIENTE_NOME, c.CLIENTE_REF)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                          {c.CLIENTE_NOME || c.CLIENTE_REF}
                        </Typography>
                        {c.CLIENTE_NOME && (
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                            {c.CLIENTE_REF}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={SERVICO_LABEL[c.TIPO_SERVICO] || c.TIPO_SERVICO}
                      size="small"
                      color={SERVICO_COLOR[c.TIPO_SERVICO] || 'default'}
                      sx={{ fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {c.CLIENTE_EMAIL || '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{c.totalTransacoes}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(c.totalVolume)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="success.main" fontWeight={700}>
                      +{fmt(c.totalComissao)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {fmtDate(c.ultimaTransacao)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={c.ESTADO === 'ATIVO' ? 'Activo' : 'Inactivo'}
                      size="small"
                      color={c.ESTADO === 'ATIVO' ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pageCount > 1 && !loading && (
        <Stack direction="row" justifyContent="center" mt={3}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
            shape="rounded"
          />
        </Stack>
      )}
    </Box>
  );
}
